<template>
  <canvas ref="canvasRef" class="hero-particles-canvas" aria-hidden="true" />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";

const canvasRef = ref(null);

const COLORS = {
  core: "#5ec8ff",
  mid: "#2a8fd4",
  dark: "#04213b",
};

const RIPPLE_LIFETIME_MS = 4500;
const PARTICLE_COUNT = 12;
const MAX_RIPPLES = 3;
const RING_COUNT = 8;
const RING_INTERVAL_MS = 380;

const MOVEMENT_MODES = ["ltr", "rtl", "tl-br", "br-tl", "tr-bl", "bl-tr"];

const pickMovementMode = () =>
  MOVEMENT_MODES[Math.floor(Math.random() * MOVEMENT_MODES.length)];

const smoothStep = (t) => t * t * (3 - 2 * t);

const drawWaterBand = (context, x, y, radius, opacity, bandWidth = 20) => {
  if (radius < 2 || opacity <= 0.002) return;

  const half = bandWidth * 0.5;
  const inner = Math.max(0.001, radius - half);
  const outer = Math.max(inner + 0.001, radius + half);

  const gradient = context.createRadialGradient(x, y, inner, x, y, outer);
  gradient.addColorStop(0, "rgba(4, 33, 59, 0)");
  gradient.addColorStop(0.18, `rgba(6, 42, 72, ${opacity * 0.22})`);
  gradient.addColorStop(0.38, `rgba(55, 140, 205, ${opacity * 0.38})`);
  gradient.addColorStop(0.5, `rgba(215, 245, 255, ${opacity * 0.82})`);
  gradient.addColorStop(0.62, `rgba(70, 165, 225, ${opacity * 0.32})`);
  gradient.addColorStop(0.82, `rgba(8, 48, 82, ${opacity * 0.14})`);
  gradient.addColorStop(1, "rgba(4, 33, 59, 0)");

  context.fillStyle = gradient;
  context.beginPath();
  context.arc(x, y, outer, 0, Math.PI * 2);
  context.fill();
};

const drawWaterSurface = (context, x, y, radius, opacity) => {
  if (radius < 4 || opacity <= 0.002) return;

  const innerR = Math.max(0.001, radius * 0.08);
  const outerR = Math.max(innerR + 0.001, radius);

  const gradient = context.createRadialGradient(x, y, innerR, x, y, outerR);
  gradient.addColorStop(0, `rgba(120, 210, 255, ${opacity * 0.1})`);
  gradient.addColorStop(0.35, `rgba(42, 130, 195, ${opacity * 0.06})`);
  gradient.addColorStop(0.72, `rgba(18, 70, 115, ${opacity * 0.03})`);
  gradient.addColorStop(1, "rgba(4, 33, 59, 0)");

  context.fillStyle = gradient;
  context.beginPath();
  context.arc(x, y, outerR, 0, Math.PI * 2);
  context.fill();
};

const drawCenterSplash = (context, x, y, elapsed, globalFade) => {
  if (elapsed >= 680 || globalFade <= 0) return;

  const t = elapsed / 680;
  const fade = (1 - smoothStep(t)) * globalFade;

  drawWaterBand(context, x, y, 8 + t * 34, fade * 0.95, 12 + t * 8);

  const crownR = Math.max(1, 5 + t * 16);
  const crownInner = Math.max(0.001, crownR * 0.55);
  const crownOuter = Math.max(crownInner + 0.001, crownR * 1.35);
  const crown = context.createRadialGradient(x, y - crownR * 0.35, crownInner, x, y, crownOuter);
  crown.addColorStop(0, `rgba(235, 250, 255, ${fade * 0.75})`);
  crown.addColorStop(0.45, `rgba(130, 210, 255, ${fade * 0.35})`);
  crown.addColorStop(1, "rgba(4, 33, 59, 0)");

  context.fillStyle = crown;
  context.beginPath();
  context.arc(x, y, crownOuter, 0, Math.PI * 2);
  context.fill();

  const dotR = Math.max(1, 2.5 + (1 - t) * 3.5);
  context.beginPath();
  context.arc(x, y - dotR * 0.4, dotR, 0, Math.PI * 2);
  context.fillStyle = `rgba(240, 252, 255, ${fade * 0.85})`;
  context.fill();
};

let ctx = null;
let width = 0;
let height = 0;
let particles = [];
let ripples = [];
let animationId = null;
let resizeObserver = null;
let lastFrameTime = 0;

class Ripple {
  constructor(x, y) {
    this.x = x;
    this.y = y;
    this.startTime = performance.now();
    this.lifetime = RIPPLE_LIFETIME_MS;
    this.maxRadius = Math.max(120, Math.max(width, height) * 0.72);
    this.waveSpeed = this.maxRadius / (this.lifetime * 0.82);
    this.rings = [];
    this.globalFade = 1;
  }

  spawnRing(index) {
    this.rings.push({
      born: this.startTime + index * RING_INTERVAL_MS,
      radius: 0,
      opacity: 0,
      depth: index,
    });
  }

  update(now = performance.now()) {
    const elapsed = now - this.startTime;
    if (elapsed >= this.lifetime) return false;

    this.globalFade = 1 - smoothStep(elapsed / this.lifetime);

    const ringTarget = Math.min(
      RING_COUNT,
      Math.floor(elapsed / RING_INTERVAL_MS) + 1
    );

    while (this.rings.length < ringTarget) {
      this.spawnRing(this.rings.length);
    }

    this.rings.forEach((ring) => {
      const ringAge = now - ring.born;
      if (ringAge <= 0) {
        ring.radius = 0;
        ring.opacity = 0;
        return;
      }

      ring.radius = ringAge * this.waveSpeed;

      const birthFade = smoothStep(Math.min(1, ringAge / 320));
      const travelFade = 1 - smoothStep(Math.pow(ring.radius / this.maxRadius, 1.15));
      const depthFade = 1 - ring.depth * 0.055;

      ring.opacity = birthFade * travelFade * this.globalFade * depthFade;
      ring.bandWidth = 14 + Math.min(12, ring.radius * 0.018);
    });

    return true;
  }

  draw(context, now = performance.now()) {
    if (this.globalFade <= 0.002) return;

    const elapsed = now - this.startTime;
    const radii = this.rings.map((ring) => ring.radius);
    const frontRadius = radii.length
      ? Math.max(...radii)
      : Math.max(0, elapsed * this.waveSpeed);

    context.save();
    context.globalCompositeOperation = "lighter";

    drawWaterSurface(
      context,
      this.x,
      this.y,
      frontRadius,
      this.globalFade * 0.85
    );

    drawCenterSplash(context, this.x, this.y, elapsed, this.globalFade);

    const visibleRings = this.rings
      .filter((ring) => ring.radius >= 2 && ring.opacity > 0.002)
      .sort((a, b) => a.radius - b.radius);

    visibleRings.forEach((ring) => {
      drawWaterBand(
        context,
        this.x,
        this.y,
        ring.radius,
        ring.opacity,
        ring.bandWidth ?? 18
      );
    });

    context.restore();
  }

  affectsParticle(particle) {
    this.rings.forEach((ring) => {
      if (ring.opacity <= 0.04 || ring.radius < 8) return;

      const dx = particle.x - this.x;
      const dy = particle.y - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const waveDist = Math.abs(dist - ring.radius);

      if (waveDist > 38) return;

      const strength = (1 - waveDist / 38) * ring.opacity * 0.75;
      particle.vx += (dx / dist) * strength * 1;
      particle.vy += (dy / dist) * strength * 1;
      particle.glowBoost = Math.min(1, particle.glowBoost + strength * 0.18);
      particle.wingPhase += strength * 0.1;
    });
  }
}

class Particle {
  constructor() {
    this.reset(true);
  }

  applyMovement(mode) {
    const speed = 0.45 + Math.random() * 0.75;
    const drift = (Math.random() - 0.5) * 0.22;

    switch (mode) {
      case "ltr":
        this.vx = speed;
        this.vy = drift;
        break;
      case "rtl":
        this.vx = -speed;
        this.vy = drift;
        break;
      case "tl-br":
        this.vx = speed * 0.82;
        this.vy = speed * 0.62 + drift * 0.35;
        break;
      case "br-tl":
        this.vx = -speed * 0.82;
        this.vy = -speed * 0.62 + drift * 0.35;
        break;
      case "tr-bl":
        this.vx = -speed * 0.82;
        this.vy = speed * 0.62 + drift * 0.35;
        break;
      case "bl-tr":
        this.vx = speed * 0.82;
        this.vy = -speed * 0.62 + drift * 0.35;
        break;
      default:
        this.vx = speed;
        this.vy = drift;
    }

    this.baseVx = this.vx;
    this.baseVy = this.vy;
  }

  placeOffScreen(mode) {
    const pad = 45;
    const edgeJitter = () => Math.random();

    switch (mode) {
      case "ltr":
        this.x = -pad;
        this.y = edgeJitter() * height;
        break;
      case "rtl":
        this.x = width + pad;
        this.y = edgeJitter() * height;
        break;
      case "tl-br":
        this.x = -pad - edgeJitter() * 60;
        this.y = -pad - edgeJitter() * 60;
        break;
      case "br-tl":
        this.x = width + pad + edgeJitter() * 60;
        this.y = height + pad + edgeJitter() * 60;
        break;
      case "tr-bl":
        this.x = width + pad + edgeJitter() * 60;
        this.y = -pad - edgeJitter() * 60;
        break;
      case "bl-tr":
        this.x = -pad - edgeJitter() * 60;
        this.y = height + pad + edgeJitter() * 60;
        break;
      default:
        this.x = Math.random() * width;
        this.y = Math.random() * height;
    }
  }

  placeRandomOnScreen() {
    this.x = Math.random() * width;
    this.y = Math.random() * height;
  }

  isOffScreen() {
    const margin = 55;
    return (
      this.x < -margin ||
      this.x > width + margin ||
      this.y < -margin ||
      this.y > height + margin
    );
  }

  reset(initial = false) {
    this.mode = pickMovementMode();
    this.applyMovement(this.mode);
    this.size = Math.random() * 3 + 2;
    this.type = Math.random() > 0.55 ? "fly" : "dot";
    this.wingPhase = Math.random() * Math.PI * 2;
    this.wingSpeed = 0.08 + Math.random() * 0.06;
    this.glowBoost = 0;
    this.opacity = 0.5 + Math.random() * 0.5;

    if (initial) {
      this.placeRandomOnScreen();
    } else {
      this.placeOffScreen(this.mode);
    }
  }

  update(dt) {
    this.vx += (this.baseVx - this.vx) * 0.018 * dt;
    this.vy += (this.baseVy - this.vy) * 0.018 * dt;

    this.x += this.vx * dt;
    this.y += (this.vy + Math.sin(this.wingPhase) * 0.12) * dt;
    this.wingPhase += this.wingSpeed * dt;
    this.glowBoost *= Math.pow(0.94, dt);

    if (this.isOffScreen()) {
      this.reset(false);
    }
  }

  draw(context) {
    const boost = this.glowBoost;
    const glowSize = Math.max(1, this.size * (3 + boost * 4));

    context.save();

    const gradient = context.createRadialGradient(
      this.x, this.y, 0,
      this.x, this.y, glowSize
    );
    gradient.addColorStop(0, `rgba(180, 230, 255, ${0.95 + boost * 0.05})`);
    gradient.addColorStop(0.25, `rgba(94, 200, 255, ${0.7 + boost * 0.25})`);
    gradient.addColorStop(0.55, `rgba(42, 143, 212, ${0.35 + boost * 0.2})`);
    gradient.addColorStop(1, "rgba(4, 33, 59, 0)");

    context.globalAlpha = this.opacity;
    context.fillStyle = gradient;

    if (this.type === "dot") {
      context.beginPath();
      context.arc(this.x, this.y, this.size + boost * 2, 0, Math.PI * 2);
      context.fill();
    } else {
      this.drawButterfly(context, boost);
    }

    context.restore();
  }

  drawButterfly(context, boost) {
    const flap = Math.sin(this.wingPhase) * 0.5;
    const wingW = (this.size + 2) * (1.8 + boost);
    const wingH = (this.size + 1) * (1.2 + boost * 0.5);

    context.fillStyle = `rgba(94, 200, 255, ${0.75 + boost * 0.2})`;

    context.beginPath();
    context.ellipse(this.x - wingW * 0.5, this.y, wingW, wingH * (0.6 + flap), -0.3, 0, Math.PI * 2);
    context.fill();

    context.beginPath();
    context.ellipse(this.x + wingW * 0.5, this.y, wingW, wingH * (0.6 - flap), 0.3, 0, Math.PI * 2);
    context.fill();

    context.fillStyle = COLORS.dark;
    context.beginPath();
    context.ellipse(this.x, this.y, 1.5, this.size + 1, 0, 0, Math.PI * 2);
    context.fill();
  }
}

const initParticles = () => {
  particles = Array.from({ length: PARTICLE_COUNT }, () => new Particle());
};

const resize = () => {
  const canvas = canvasRef.value;
  if (!canvas) return;

  const parent = canvas.parentElement;
  width = parent?.clientWidth || window.innerWidth;
  height = parent?.clientHeight || window.innerHeight;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  ctx = canvas.getContext("2d", { alpha: true });
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(dpr, dpr);
  ctx.imageSmoothingEnabled = true;

  if (particles.length === 0) initParticles();
};

const animate = (now = performance.now()) => {
  if (!ctx) return;

  if (!lastFrameTime) lastFrameTime = now;
  const dt = Math.min((now - lastFrameTime) / 16.667, 2.5);
  lastFrameTime = now;

  ctx.clearRect(0, 0, width, height);
  ctx.globalCompositeOperation = "source-over";

  ripples = ripples.filter((ripple) => {
    const alive = ripple.update(now);
    if (alive) ripple.draw(ctx, now);
    return alive;
  });

  particles.forEach((particle) => {
    ripples.forEach((ripple) => ripple.affectsParticle(particle));
    particle.update(dt);
    particle.draw(ctx);
  });

  animationId = requestAnimationFrame(animate);
};

const addRipple = (x, y) => {
  if (width < 1 || height < 1) return;

  if (ripples.length >= MAX_RIPPLES) {
    ripples.splice(0, ripples.length - MAX_RIPPLES + 1);
  }

  ripples.push(new Ripple(x, y));
};

defineExpose({ addRipple });

onMounted(() => {
  resize();
  animate();

  resizeObserver = new ResizeObserver(resize);
  if (canvasRef.value?.parentElement) {
    resizeObserver.observe(canvasRef.value.parentElement);
  }
});

onBeforeUnmount(() => {
  cancelAnimationFrame(animationId);
  resizeObserver?.disconnect();
});
</script>

<style scoped>
.hero-particles-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}
</style>
